import React from 'react';
import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import {
  gray5,
  gray40,
  gray80,
  gray100,
  primaryButtonBackgroundColor,
} from '../../../constants/colors';
import { dialogTitle, regularMd, regularSm } from '../../../constants/location';
import { emailMessageStatus } from '../../../constants/enum';
import { EmailMessage } from '../../../types/emailImport';
import { useProxyDialogSx } from '../../../hooks/useProxyDialogSx';
import EmailStatusLabel from './EmailStatusLabel';

interface Props {
  open: boolean;
  message: EmailMessage | null;
  /** 先頭結果が「該当なし(other)」か。true のとき結果ページを開けないためボタンを非活性にする（#3154） */
  isOtherResult: boolean;
  onClose: () => void;
  /** 「OCR結果を見る」押下時の遷移（一覧側で resultId を解決して遷移する） */
  onViewOcrResult: (message: EmailMessage) => void;
}

// design-rules.md「8. ダイアログ」に準拠。詳細は縦長になり得るため本文をスクロールさせる
const styles = {
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      width: '640px',
      maxWidth: 'calc(100% - 32px)',
      '@media screen and (max-width:960px)': {
        padding: '32px 16px',
      },
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  dialogTitle,
  body: {
    maxHeight: '60vh',
    overflowY: 'auto' as const,
    paddingRight: '24px',
    marginRight: '-24px',
  },
  statusRow: {
    marginBottom: '24px',
  },
  infoList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
    marginBottom: '24px',
  },
  infoRow: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'flex-start',
    gap: '16px',
  },
  infoLabel: {
    ...regularSm,
    color: gray80,
    flexShrink: 0,
    paddingTop: '2px',
    width: '96px',
  },
  infoValue: {
    ...regularMd,
    color: gray100,
    wordBreak: 'break-all' as const,
  },
  bodyLabel: {
    ...regularSm,
    color: gray80,
    marginBottom: '8px',
  },
  bodyText: {
    ...regularMd,
    backgroundColor: gray5,
    borderRadius: '4px',
    color: gray100,
    maxHeight: '240px',
    overflowY: 'auto' as const,
    padding: '16px',
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-all' as const,
  },
  bodyEmpty: {
    ...regularMd,
    color: gray80,
  },
  footer: {
    alignItems: 'center',
    display: 'flex',
    gap: '16px',
    justifyContent: 'flex-end',
    marginTop: '32px',
  },
  noPatternText: {
    ...regularSm,
    color: gray80,
  },
  ocrButton: {
    height: '44px',
    padding: '0 24px',
  },
};

/**
 * 受信メールの詳細モーダル（#2965 Phase2）。
 * 一覧では持たない本文全文・対象外理由を表示し、
 * 取り込み完了メールには OCR 結果への導線を用意する。
 */
const EmailMessageDetailDialog: React.FC<Props> = ({
  open,
  message,
  isOtherResult,
  onClose,
  onViewOcrResult,
}) => {
  // hooks は早期 return より前で呼ぶ（代理モード時のバナー分 marginTop を付与）
  const proxyDialogSx = useProxyDialogSx();
  if (!message) return null;

  const canViewOcrResult =
    message.status === emailMessageStatus.completed && message.ocr_job_ids?.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} sx={[styles.dialog, proxyDialogSx]}>
      <Box sx={styles.dialogHeader}>
        <Typography sx={styles.dialogTitle}>メール詳細</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={styles.body}>
        {message.status !== emailMessageStatus.completed && (
          <Box sx={styles.statusRow}>
            <EmailStatusLabel message={message} />
          </Box>
        )}

        <Box sx={styles.infoList}>
          <Box sx={styles.infoRow}>
            <Typography sx={styles.infoLabel}>受信日時</Typography>
            <Typography sx={styles.infoValue}>
              {dayjs(message.received_at.toDate()).format('YYYY/MM/DD HH:mm')}
            </Typography>
          </Box>
          <Box sx={styles.infoRow}>
            <Typography sx={styles.infoLabel}>送信元</Typography>
            <Typography sx={styles.infoValue}>{message.from}</Typography>
          </Box>
          <Box sx={styles.infoRow}>
            <Typography sx={styles.infoLabel}>件名</Typography>
            <Typography sx={styles.infoValue}>{message.subject || '(件名なし)'}</Typography>
          </Box>
          <Box sx={styles.infoRow}>
            <Typography sx={styles.infoLabel}>添付ファイル</Typography>
            <Typography sx={styles.infoValue}>{message.attachment_count} 件</Typography>
          </Box>
        </Box>

        <Typography sx={styles.bodyLabel}>本文</Typography>
        {message.body_text ? (
          <Box sx={styles.bodyText}>{message.body_text}</Box>
        ) : (
          <Typography sx={styles.bodyEmpty}>本文はありません。</Typography>
        )}
      </Box>

      {canViewOcrResult && (
        <Box sx={styles.footer}>
          {isOtherResult && (
            <Typography sx={styles.noPatternText}>該当するパターンがありません</Typography>
          )}
          <Button
            onClick={(): void => onViewOcrResult(message)}
            disabled={isOtherResult}
            sx={[primaryButtonBackgroundColor, styles.ocrButton]}
          >
            OCR結果を見る
          </Button>
        </Box>
      )}
    </Dialog>
  );
};

export default EmailMessageDetailDialog;
